import { Injectable, computed, signal } from '@angular/core';
import { SparkChartMetric } from '@/app/common/general-types/spark-ads-v3.dto';
import { SparkDateRange } from './spark-ads-reporting.service';
import {
  SPARK_RANGE_PRESETS,
  SparkAdsView,
  SparkLibraryFilter,
  SparkRangePreset,
} from '../spark-ads.types';

/**
 * View state shared across the three Spark Ads screens.
 *
 * Only presentation state lives here — anything fetched belongs to the screen that
 * fetches it. Kept root-provided so the dashboard, builder and library agree on the
 * selected date range and so the library can hand a post to the builder.
 */
@Injectable({ providedIn: 'root' })
export class SparkAdsStore {
  /** Which screen is showing. Kept in step with the `:subTab` route segment. */
  readonly view = signal<SparkAdsView>('dashboard');

  /** Dashboard date range. */
  readonly rangePreset = signal<SparkRangePreset>('14d');

  /** Dashboard chart metric. */
  readonly chartMetric = signal<SparkChartMetric>('spend');

  /** Expanded rows in the campaign tree, keyed by node id. */
  readonly openTreeRows = signal<Record<string, boolean>>({});

  /** Library filter pill. */
  readonly libraryFilter = signal<SparkLibraryFilter>('all');

  /**
   * A post the library sent to the builder, so "Create an ad group for this post"
   * carries the post through instead of losing it.
   */
  readonly carriedVideoId = signal<string | undefined>(undefined);

  /** Resolved `YYYY-MM-DD` window for the selected preset, ending today. */
  readonly dateRange = computed<SparkDateRange>(() => {
    const preset = SPARK_RANGE_PRESETS.find((option) => option.key === this.rangePreset());
    const days = preset?.days ?? 14;
    const end = new Date();
    const start = new Date(end.getTime() - (days - 1) * 86400000);
    return { startDate: isoDate(start), endDate: isoDate(end) };
  });

  setView(view: SparkAdsView): void {
    this.view.set(view);
  }

  setRangePreset(preset: SparkRangePreset): void {
    this.rangePreset.set(preset);
  }

  setChartMetric(metric: SparkChartMetric): void {
    this.chartMetric.set(metric);
  }

  setLibraryFilter(filter: SparkLibraryFilter): void {
    this.libraryFilter.set(filter);
  }

  toggleTreeRow(nodeId: string): void {
    this.openTreeRows.update((open) => ({ ...open, [nodeId]: !open[nodeId] }));
  }

  isTreeRowOpen(nodeId: string): boolean {
    return this.openTreeRows()[nodeId] === true;
  }

  /** Opens every node id passed in — used by "Expand all". */
  expandTreeRows(nodeIds: string[]): void {
    this.openTreeRows.set(Object.fromEntries(nodeIds.map((id) => [id, true])));
  }

  collapseTreeRows(): void {
    this.openTreeRows.set({});
  }

  carryVideoToBuilder(videoId: string): void {
    this.carriedVideoId.set(videoId);
  }

  clearCarriedVideo(): void {
    this.carriedVideoId.set(undefined);
  }
}

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}
